import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Bell, Key, Megaphone, Users, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { AuthService } from '@/services/core/auth'
import { PoliticalCampaignsApi } from '@/services/political/politicalCampaignsApi'
import { AppShell } from '../components/AppShell'
import { CampaignTable } from '../components/CampaignTable'
import { CallToAction } from '../components/CallToAction'
import { PoliticalEntitiesApi } from '@/services/political/politicalEntitiesApi'
import { useNotifications } from '../contexts/NotificationsContext'

function newCampaignUrl() {
  return `/political/campaigns/${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}?new=true`
}

export function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hasToken = AuthService.isAuthenticated()

const { notifications, count: notificationCount } = useNotifications()

  const { data: campaigns = [], isLoading: loadingCampaigns } = useQuery({
    queryKey: ['politicalCampaigns'],
    queryFn: () => PoliticalCampaignsApi.getAll(),
    enabled: hasToken,
  })

  const { data: entities = [] } = useQuery({
    queryKey: ['politicalEntities'],
    queryFn: () => PoliticalEntitiesApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })

  const recentCampaigns = [...campaigns]
    .sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime())
    .slice(0, 5)

  return (
    <AppShell crumbs={[{ label: 'Home' }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('dashboard.title')}</h1>
            <p>{t('dashboard.subtitle')}</p>
          </div>
        </div>

        <CallToAction
          icon="pi-megaphone"
          title={t('dashboard.ctaTitle')}
          body={t('dashboard.ctaBody')}
          onClick={() => navigate(newCampaignUrl())}
        />

        {notificationCount > 0 && (
          <div className="ad-notification-banner">
            <div className="ad-notification-banner__icon">
              <Bell className="size-4" />
              <span className="ad-notification-banner__count">{notificationCount}</span>
            </div>
            <div className="ad-notification-banner__content">
              <div className="ad-notification-banner__title">
                {t('notifications.pendingTitle', { count: notificationCount })}
              </div>
              <div className="ad-notification-banner__body">
                {notifications.map((n) => (
                  <div key={n.id} className="ad-notification-banner__item">
                    <Key className="size-4" />
                    <span>{n.name}</span>
                    <span className="ad-notification-banner__advertiser">
                      {n.advertiser?.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* KPI row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div
            className="ad-cta-card ad-cta-card--surface"
            onClick={() => navigate('/political/campaigns')}
          >
            <div className="ad-cta-card__icon ad-cta-card__icon--surface">
              <Megaphone className="size-4" />
            </div>
            <div className="ad-cta-card__content">
              <div className="ad-cta-card__value">
                {loadingCampaigns ? <Skeleton className="h-6 w-8" /> : campaigns.length}
              </div>
              <div className="ad-cta-card__body ad-cta-card__body--surface">
                {t('dashboard.totalCampaigns')}
              </div>
            </div>
            <ArrowRight className="size-4 ad-cta-card__arrow ad-cta-card__arrow--surface" />
          </div>
          <div
            className="ad-cta-card ad-cta-card--surface"
            onClick={() => navigate('/political/entities')}
          >
            <div className="ad-cta-card__icon ad-cta-card__icon--surface">
              <Users className="size-4" />
            </div>
            <div className="ad-cta-card__content">
              <div className="ad-cta-card__value">
                {entities.length}
              </div>
              <div className="ad-cta-card__body ad-cta-card__body--surface">
                {t('dashboard.politicalEntities')}
              </div>
            </div>
            <ArrowRight className="size-4 ad-cta-card__arrow ad-cta-card__arrow--surface" />
          </div>
        </div>

        {/* Recent campaigns */}
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Megaphone} />
              {t('dashboard.recentCampaigns')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/political/campaigns')}
            >
              {t('dashboard.viewAll')}
            </Button>
          </CardHeader>

          {loadingCampaigns ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full rounded-md" />
              ))}
            </div>
          ) : recentCampaigns.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px 24px' }}>
              <Megaphone className="size-6" />
              <div className="empty-state__title">{t('dashboard.noCampaignsTitle')}</div>
              <div className="empty-state__body">{t('dashboard.noCampaignsBody')}</div>
            </div>
          ) : (
            <CardContent className="p-0">
              <CampaignTable
                campaigns={recentCampaigns}
                loading={loadingCampaigns}
                emptyMessage={t('dashboard.noCampaignsFound')}
              />
            </CardContent>
          )}
        </Card>
      </div>
    </AppShell>
  )
}
